// src/pages/Support/components/SupportCenter.js

import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Button, Card, Flex, message, Modal, Spin, Alert } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  PlusOutlined,
  CustomerServiceOutlined,
  BugOutlined,
  BulbOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";

import StatsCards from "./StatsCards.js";
import FilterBar from "./FilterBar.js";
import TicketTable from "./TicketTable.js";
import {
  getAllCompanySupportTicketAction,
  updateSupportTicketAction,
  createSupportTicketAction,
} from "../../../../store/action/admin/supportTicketActions.js";
import CustomLoaderIcon from "../../../../Theme/CustomIcons/Icons/Essetional/CustomLoaderIcon.js";
import HelpdeskIcon from "../../../../Theme/CustomIcons/Icons/CustomIcons/HelpdeskIcon.js";
import MessageQuestionIcon from "../../../../Theme/CustomIcons/Icons/Support,Like,Question/MessageQuestionIcon.js";
import MessageEditIcon from "../../../../Theme/CustomIcons/Icons/CustomIcons/MessageEditIcon.js";

// Ticket type options configuration
const TICKET_TYPES = [
  {
    key: "Help & Support",
    label: "Help & Support",
    description: "General help and assistance",
    icon: <HelpdeskIcon style={{ fontSize: 28, color: "#1890ff" }} />,

    color: "#e6f7ff",
    borderColor: "#91d5ff",
  },
  {
    key: "Technical Issue",
    label: "Technical Issue",
    description: "Report a bug or technical problem",
    icon: <MessageQuestionIcon style={{ fontSize: 28, color: "#f5222d" }} />,
    color: "#fff1f0",
    borderColor: "#ffa39e",
  },
  {
    key: "Feature Request",
    label: "Feature Request",
    description: "Suggest a new feature or improvement",
    icon: <MessageEditIcon style={{ fontSize: 28, color: "#faad14" }} />,
    color: "#fffbe6",
    borderColor: "#ffd666",
  },
  {
    key: "Other",
    label: "Other",
    description: "Any other type of inquiry",
    icon: <QuestionCircleOutlined style={{ fontSize: 28, color: "#722ed1" }} />,
    color: "#f9f0ff",
    borderColor: "#d3adf7",
  },
];

const SupportCenter = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const themeDefault = useSelector((state) => state.themeDefault);

  const tokenInfoList = useSelector((state) => state.tokenInfoList);
  const getAllCompanySupportList = useSelector(
    (state) => state.getAllCompanySupportList,
  );

  const {
    loading: ticketsLoading,
    info: ticketsData,
    error: ticketsError,
  } = getAllCompanySupportList;

  const {
    loading: createTicketLoading = false,
    info: createTicketInfo = null,
    error: createTicketError = null,
  } = useSelector((state) => state.createSupportTicketList || {});
  // Local State
  const [tickets, setTickets] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const [createError, setCreateError] = useState(null);
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [filters, setFilters] = useState({
    search: "",
    priority: "",
    status: "",
    fromDate: "",
    toDate: "",
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);

  const processedTicketId = useRef(null);
  const { confirm } = Modal;

  // Get token and companyId
  const token = tokenInfoList?.info?.token;
  const companyId =
    tokenInfoList?.info?.company?._id || tokenInfoList?.info?.companyId;

  // ============================================
  //  FETCH DATA ON MOUNT & FILTER/PAGE CHANGE
  // ============================================
  useEffect(() => {
    fetchTickets();
  }, [dispatch, pagination.current, filters.status, filters.priority]);

  // Handle create ticket success - navigate to details
  // Handle create ticket success - navigate to details
  useEffect(() => {
    if (createTicketInfo && !createTicketLoading) {
      const ticketId = createTicketInfo?.data?._id || createTicketInfo?._id;
      if (ticketId && processedTicketId.current !== ticketId) {
        processedTicketId.current = ticketId; // ID save kar lo

        message.success("Ticket created successfully!");
        setIsCreateModalOpen(false);
        setSelectedType(null);

        navigate(`/admin/support-details/${btoa(ticketId)}`, {
          state: { ticketData: createTicketInfo },
        });
      }
    }
  }, [createTicketInfo, createTicketLoading, navigate]);
  // Handle create ticket error
  useEffect(() => {
    if (createTicketError && !createTicketLoading) {
      setSelectedType(null);
    }
  }, [createTicketError, createTicketLoading]);

  const fetchTickets = async () => {
    try {
      setFetchError(null);
      const query = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters,
      };

      const result = await dispatch(
        getAllCompanySupportTicketAction(query, token, companyId),
      );

      if (result && result.data) {
        const ticketsArray = result.data.data || [];
        const paginationInfo = result.data.pagination || {};

        setTickets(ticketsArray);
        setPagination((prev) => ({
          ...prev,
          total: paginationInfo.total || 0,
        }));
      }
    } catch (error) {
      console.error("❌ Failed to fetch tickets:", error);
      setFetchError(error.message || "Failed to load support tickets");
    }
  };

  // ============================================
  // FILTERING LOGIC (Client-side for instant search)
  // ============================================
  const filteredTickets = useMemo(() => {
    return tickets.filter((ticket) => {
      const matchesSearch =
        !filters.search ||
        (ticket.ticketId &&
          ticket.ticketId
            .toLowerCase()
            .includes(filters.search.toLowerCase())) ||
        (ticket.subject &&
          ticket.subject.toLowerCase().includes(filters.search.toLowerCase()));

      const matchesPriority =
        !filters.priority || ticket.priority === filters.priority;
      const matchesStatus = !filters.status || ticket.status === filters.status;

      return matchesSearch && matchesPriority && matchesStatus;
    });
  }, [tickets, filters]);

  // ============================================
  // EVENT HANDLERS
  // ============================================
  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));

    if (key !== "search") {
      setPagination((prev) => ({ ...prev, current: 1 }));
    }
  }, []);

  // Open Create Ticket Modal
  const handleOpenCreateModal = useCallback(() => {
    setSelectedType(null);
    setCreateError(null);
    setIsCreateModalOpen(true);
  }, []);

  // Close Create Ticket Modal
  const handleCloseCreateModal = useCallback(() => {
    if (!createTicketLoading) {
      setIsCreateModalOpen(false);
      setSelectedType(null);
      setCreateError(null);
    }
  }, [createTicketLoading]);

  // Handle Ticket Type Selection
  const handleTypeSelect = useCallback(
    async (type) => {
      if (createTicketLoading) return;

      setSelectedType(type);
      setCreateError(null);

      try {
        const body = {
          subject: type,
        };

        await dispatch(createSupportTicketAction(body, token, companyId, true));
      } catch (error) {
        setSelectedType(null);
        setCreateError(error.message || "Failed to create ticket");
      }
    },
    [createTicketLoading, dispatch, token, companyId],
  );

  // MARK AS RESOLVED/CLOSED
  const handleMarkResolved = useCallback(
    (ticketId, ticketSubject, newStatus = "closed", ticketNumber) => {
      confirm({
        title: `Update ticket ${ticketNumber}?`,
        content: `This will change the status of "${ticketSubject}" to closed. You won't be able to send messages after this.`,
        okText: `Yes, mark as ${newStatus}`,
        okType: "primary",
        cancelText: "Cancel",
        onOk: async () => {
          try {
            await dispatch(
              updateSupportTicketAction(
                { status: newStatus },
                ticketId,
                null,
                null,
                true,
              ),
            );

            message.success(`Ticket ${ticketNumber} marked as ${newStatus}!`);

            fetchTickets();
          } catch (error) {
            message.error(error.message || "Failed to update ticket status");
          }
        },
      });
    },
    [confirm, dispatch],
  );

  // Handle page change
  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, current: page }));
  };

  // Loading State
  if (ticketsLoading && tickets.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "100px 0" }}>
        <CustomLoaderIcon size="large" />
        <p style={{ marginTop: 16 }}>Loading support tickets...</p>
      </div>
    );
  }

  return (
    <div className="support-center-container">
      {/* Statistics Cards */}
      <StatsCards tickets={filteredTickets} />

      {/* Filter Bar */}
      <FilterBar filters={filters} onFilterChange={handleFilterChange} />

      {/* Fetch/List Error Alert */}
      {(fetchError || ticketsError) && (
        <Alert
          message="Failed to load support tickets"
          description={fetchError || ticketsError}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          closable
          onClose={() => setFetchError(null)}
        />
      )}

      {/* Ticket Table - Button next to "All Tickets" */}
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>All Tickets</span>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleOpenCreateModal}
              size="middle"
              style={{
                borderRadius: 8,
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}>
              Create Ticket
            </Button>
          </div>
        }
        style={{
          borderTop: themeDefault?.info?.newConfig?.cardborder,
        }}>
        <TicketTable
          tickets={filteredTickets}
          selectedTickets={selectedTickets}
          onSelectTicket={(id) =>
            setSelectedTickets((prev) =>
              prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
            )
          }
          onSelectAll={(ids) => setSelectedTickets(ids)}
          onMarkResolved={handleMarkResolved}
          pagination={pagination}
          onPageChange={handlePageChange}
          loading={ticketsLoading}
        />
      </Card>

      {/* Create Ticket Modal */}
      <Modal
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontSize: 18,
              fontWeight: 600,
            }}>
            <PlusOutlined style={{ color: themeDefault?.info?.colorPrimary }} />
            Create New Ticket
          </div>
        }
        open={isCreateModalOpen}
        onCancel={handleCloseCreateModal}
        footer={null}
        width={600}
        closable={!createTicketLoading}
        maskClosable={!createTicketLoading}
        styles={{
          body: { padding: "24px 24px 16px" },
        }}>
        {/* Create Ticket Error Alert */}
        {(createError || createTicketError) && (
          <Alert
            message="Failed to create ticket"
            description={createError || createTicketError}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            closable
            onClose={() => setCreateError(null)}
          />
        )}

        <p
          style={{
            marginBottom: 20,
            fontSize: 14,
          }}>
          Select the type of support you need:
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 16,
          }}>
          {TICKET_TYPES.map((type) => {
            const isSelected = selectedType === type.key;
            const isLoading = createTicketLoading && isSelected;

            return (
              <div
                key={type.key}
                onClick={() => handleTypeSelect(type.key)}
                style={{
                  padding: "20px 16px",
                  borderRadius: 12,
                  border: `2px solid ${isSelected ? themeDefault?.info?.colorPrimary : type.borderColor}`,
                  backgroundColor: isLoading
                    ? "#f5f5f5"
                    : isSelected
                      ? themeDefault?.info?.colorPrimary + "10"
                      : type.color,
                  cursor: createTicketLoading ? "not-allowed" : "pointer",
                  transition: "all 0.3s ease",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  gap: 10,
                  position: "relative",
                  opacity: createTicketLoading && !isSelected ? 0.5 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!createTicketLoading) {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 12px rgba(0,0,0,0.1)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}>
                {isLoading && (
                  <div
                    style={{
                      position: "absolute",
                      top: 10,
                      right: 10,
                    }}>
                    <CustomLoaderIcon size="small" />
                  </div>
                )}

                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  }}>
                  {type.icon}
                </div>

                <div>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 15,
                      marginBottom: 4,
                      color: isSelected
                        ? themeDefault?.info?.colorPrimary
                        : "#262626",
                    }}>
                    {type.label}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#8c8c8c",
                      lineHeight: 1.4,
                    }}>
                    {type.description}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer note */}
        <div
          style={{
            marginTop: 16,
            textAlign: "center",
            fontSize: 12,
          }}>
          Click on any option to create a ticket instantly
        </div>
      </Modal>
    </div>
  );
};

export default SupportCenter;
